import { NavLink, useLocation } from '@remix-run/react'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { SiInstagram, SiLinkedin } from 'react-icons/si'
import { LINKS, SOCIALS } from '#app/config/constants'
import IconLink from './iconLink'

type Item = {
	to: string
	name: string | React.ReactNode
}

const MenuItem = (props: {
	item: Item
	handleNavClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
}) => {
	const location = useLocation()

	const isSelected =
		props.item.to === location.pathname ||
		location.pathname.startsWith(`${props.item.to}/`)
	return (
		<NavLink to={props?.item?.to} onClick={props.handleNavClick}>
			<li
				className={clsx(
					'rounded-b-lg border-t-4 border-t-transparent px-6 py-4 uppercase transition duration-150 hover:bg-secondary',
					{
						active: isSelected,
						'bg-secondary': isSelected,
					},
				)}
			>
				{props.item.name}
			</li>
		</NavLink>
	)
}

export default function Navbar() {
	const location = useLocation()
	const navRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const handleScroll = () => {
			if (navRef.current) {
				if (window.scrollY > 220) {
					navRef.current.classList.add('shadow-md')
				} else {
					navRef.current.classList.remove('shadow-md')
				}
			}
		}
		window.addEventListener('scroll', handleScroll)

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	useEffect(() => {
		function sethighlight() {
			const activeNav = document.getElementsByClassName('active')
			const tabHighlighter = document.getElementById('tab-highlighter')
			if (tabHighlighter !== null) {
				tabHighlighter.style.left = activeNav[0]?.offsetLeft + 'px'
				tabHighlighter.style.width = activeNav[0]?.offsetWidth + 'px'
			}
		}
		sethighlight()

		window.addEventListener('resize', sethighlight)

		if (document.readyState === 'complete') {
			sethighlight()
		} else {
			window.addEventListener('onreadystate', sethighlight)
			// Remove the event listener when component unmounts
			return () => {
				window.removeEventListener('onreadystate', sethighlight)
				window.removeEventListener('resize', sethighlight)
			}
		}
	}, [location])

	const handleNavClick = (event: any) => {
		const tabHighlighter = document.getElementById('tab-highlighter')
		if (tabHighlighter) {
			tabHighlighter.style.left = event.target.closest('li').offsetLeft + 'px'
			tabHighlighter.style.width = event.target.closest('li').offsetWidth + 'px'
		}
	}

	return (
		<nav
			ref={navRef}
			className="sticky top-0 z-50 mx-auto hidden bg-white lg:row-span-1 lg:block"
		>
			<div className="mx-auto flex w-[95%] flex-col border-t-2 border-t-gray-300 xl:w-11/12 2xl:w-10/12">
				<ul className="mx-auto mb-4 flex">
					{LINKS.map((item, i) => (
						<MenuItem
							item={item}
							handleNavClick={handleNavClick}
							key={`${i}+${item.name}`}
						/>
					))}
					<div className="flex gap-2 border-t-2 border-t-transparent px-6 pt-4">
						<IconLink target={SOCIALS.instagram}>
							<SiInstagram size={'24px'} />
						</IconLink>
						<IconLink target={SOCIALS.linkedin}>
							<SiLinkedin size={'24px'} />
						</IconLink>
					</div>
				</ul>
				<div id="tab-highlighter" className="tab-highlighter" />
			</div>
		</nav>
	)
}
